"use client";
import { useField } from "formik";
import { ComponentProps, ReactNode } from "react";
import { VariantProps, cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";

export const inputVariants = cva(
  "w-full min-w-[100px] p-4 text-[14px] border border-[#ccc] rounded-[8px] outline-none ring-0 text-primary-900 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-none shadow-none",
  {
    variants: {
      variant: {
        default:
          "bg-white placeholder-primary-text-200 data-[placeholder]:text-primary-text-200 hover:bg-white",
        primary:
          "bg-primary-50 placeholder-primary-text-200 data-[placeholder]:text-primary-text-200  shadow-[0px_1.72px_3.45px_0px_rgba(16,_24,_40,_0.05)] border-none",
      },
      radius: {
        sm: "rounded-[8px]",
        full: "rounded-full px-[24px]",
        roundedLeft: "rounded-l-full",
        roundedRight: "rouned-r-full",
      },
      size: {
        default: "h-[44px]",
        sm: "h-[40px] text-xs",
        lg: "h-12",
        xl: "h-[52px]",
        "2xl": "h-[73px] text-base",
      },
    },
    defaultVariants: {
      variant: "default",
      radius: "full",
      size: "default",
    },
  },
);

interface TextInputProps
  extends VariantProps<typeof inputVariants>,
    Omit<ComponentProps<"input">, "size" | "value"> {
  name: string;
  label?: string;
  isLoading?: boolean;
  helperText?: string;
  children?: ReactNode;
  placeholder?: string;
  type?: string;
  className?: string;
  inputClassName?: string;
}

export const TextInput = ({
  name,
  label,
  children,
  variant,
  size,
  radius,
  isLoading,
  helperText,
  className = "",
  type = "text",
  inputClassName = "",
  ...props
}: TextInputProps) => {
  const [field, meta] = useField(name);
  const hasError = meta.touched && meta.error;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Scroll to the input element when there's an error
    if (hasError && inputRef.current) {
      inputRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [hasError]);

  return (
    <div className={cn("w-full relative", className)}>
      <label
        htmlFor={name}
        className="block text-xs sm:text-sm lg:text-base mb-1 text-primary-text-600"
      >
        {label}
        {children}
      </label>
      <div className="relative">
        <input
          type={type}
          id={name}
          {...field}
          {...props}
          ref={inputRef}
          className={cn(
            inputVariants({ size, variant, radius }),
            hasError &&
              "!border-red-500 text-red-500 placeholder:!text-red-500",
            isLoading && "pr-12", // Add padding for spinner
            inputClassName,
          )}
        />
        {isLoading && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2">
            <Loader2 className="h-5 w-5 animate-spin text-primary-500" />
          </div>
        )}
      </div>
      {/* Error message */}
      {hasError && (
        <p className="absolute -bottom-2 left-0 h-2 text-xs text-red-500 pl-3 mt-1">
          {meta.error}
        </p>
      )}
      {/* Helper text (when not error and helperText is provided) */}
      {!hasError && helperText && (
        <p className="absolute -bottom-2 left-0 h-2 text-xs text-gray-500 pl-3 mt-1">
          {helperText}
        </p>
      )}
    </div>
  );
};
